import React, { useState, useEffect } from 'react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer
} from 'recharts';
import api from '../api/api';
import Sidebar from '../components/Sidebar';
import { useTranslation } from 'react-i18next';

const Analytics = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [stats, setStats] = useState({
        monthlyData: [],
        categoryData: [],
        typeData: []
    });

    const [reservationStats, setReservationStats] = useState({
        topVoitures: [],
        leastUsedCars: [],
        revenusMensuels: [],
        topClients: [],
        dureeMoyenne: 0,
        tauxAnnulation: 0
    });

    const [loading, setLoading] = useState({
        financial: true,
        reservations: true
    });
    const [error, setError] = useState({
        financial: null,
        reservations: null
    });
    const [timeRange, setTimeRange] = useState('month');

    useEffect(() => {
        fetchFinances();
        fetchReservationsStats();
    }, [timeRange]);

    const fetchFinances = async () => {
        try {
            setLoading(prev => ({ ...prev, financial: true }));
            setError(prev => ({ ...prev, financial: null }));

            const res = await api.get(`/transactions/analytics?range=${timeRange}`);
            setStats({
                monthlyData: res.data.monthly || [],
                categoryData: res.data.categories || [],
                typeData: res.data.types || []
            });
        } catch (err) {
            setError(prev => ({ ...prev, financial: err.message }));
            console.error("Financial stats error:", err);
        } finally {
            setLoading(prev => ({ ...prev, financial: false }));
        }
    };

    const fetchReservationsStats = async () => {
        try {
            setLoading(prev => ({ ...prev, reservations: true }));
            setError(prev => ({ ...prev, reservations: null }));

            const endpoints = [
                'top-voitures',
                'least-used-cars',
                'revenus-mensuels',
                'top-clients',
                'duree-moyenne',
                'annulation-stats'
            ];

            const responses = await Promise.all(
                endpoints.map(endpoint =>
                    api.get(`/statistics/${endpoint}`)
                        .catch(e => {
                            console.error(`Error fetching ${endpoint}:`, e.response?.data || e.message);
                            return { data: null, error: true };
                        })
                )
            );

            responses.forEach((res, index) => {
                if (res.error) {
                    console.error(`Endpoint ${endpoints[index]} failed`);
                }
            });

            setReservationStats({
                topVoitures: responses[0]?.data || [],
                leastUsedCars: responses[1]?.data || [],
                revenusMensuels: responses[2]?.data || [],
                topClients: responses[3]?.data || [],
                dureeMoyenne: responses[4]?.data?.duree_moyenne || 0,
                tauxAnnulation: responses[5]?.data?.taux_annulation || 0
            });
        } catch (err) {
            setError(prev => ({ ...prev, reservations: err.message }));
            console.error("Full error details:", {
                message: err.message,
                response: err.response?.data,
                config: err.config
            });
        } finally {
            setLoading(prev => ({ ...prev, reservations: false }));
        }
    };

    const encaissement = stats.typeData.find(t => t.type === 'encaissement')?.total || 0;
    const decaissement = stats.typeData.find(t => t.type === 'décaissement')?.total || 0;
    const ratio = encaissement ? ((decaissement / encaissement) * 100).toFixed(1) : 0;

    let cumulative = 0;
    const soldeCumule = stats.monthlyData.map(entry => {
        cumulative += (entry.encaissement - entry.décaissement);
        return { ...entry, solde: cumulative };
    });

    const maxEnc = Math.max(...stats.monthlyData.map(e => e.encaissement), 0);
    const maxDec = Math.max(...stats.monthlyData.map(e => e.décaissement), 0);
    const jourMaxEnc = stats.monthlyData.find(e => e.encaissement === maxEnc)?.period;
    const jourMaxDec = stats.monthlyData.find(e => e.décaissement === maxDec)?.period;

    if (loading.financial && loading.reservations) {
        return (
            <Sidebar>
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <h2 className="text-xl font-semibold text-gray-700">{t('loading_stats')}</h2>
                    </div>
                </div>
            </Sidebar>
        );
    }

    return (
        <Sidebar>
            <div className="bg-gray-50 min-h-screen p-6" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className={`mb-8 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <h1 className="text-3xl font-bold text-gray-800">{t('analytics_dashboard')}</h1>
                        <p className="text-gray-600 mt-2">{t('fleet_stats_desc')}</p>
                    </div>

                    {/* Time Range Selector */}
                    <div className={`flex flex-wrap gap-2 mb-8 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        {['day', 'week', 'month', 'year'].map(range => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${timeRange === range
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                {t(range === 'day' ? 'today' : range === 'week' ? 'this_week' : range === 'month' ? 'this_month' : 'this_year')}
                            </button>
                        ))}
                    </div>

                    {/* Financial Stats Section */}
                    <div className="mb-12">
                        <div className={`flex items-center justify-between mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h2 className="text-xl font-semibold text-gray-800">{t('financial_stats')}</h2>
                            <div className={`flex items-center ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                                <span className="text-sm text-gray-500">{t('performance')}</span>
                            </div>
                        </div>

                        {error.financial ? (
                            <div className={`bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6 ${isRtl ? 'text-right' : 'text-left'}`}>
                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <svg className={`w-5 h-5 text-red-500 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="font-bold text-red-700">{t('error')}</p>
                                        <p className="text-red-600">{error.financial}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <StatCard
                                        title={t('encaissements')}
                                        value={encaissement}
                                        t={t}
                                        icon={
                                            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        }
                                        trend="up"
                                    />
                                    <StatCard
                                        title={t('decaissements')}
                                        value={decaissement}
                                        t={t}
                                        icon={
                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        }
                                        trend="down"
                                    />
                                    <StatCard
                                        title={t('net_balance')}
                                        value={encaissement - decaissement}
                                        t={t}
                                        icon={
                                            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                            </svg>
                                        }
                                        trend={encaissement - decaissement >= 0 ? "up" : "down"}
                                    />
                                    <StatCard
                                        title={t('expense_income_ratio')}
                                        value={`${ratio}%`}
                                        t={t}
                                        icon={
                                            <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        }
                                    />
                                </div>

                                {/* Financial Chart */}
                                <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                                    <div className={`flex items-center justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <h3 className="text-lg font-semibold text-gray-800">{t('financial_evolution')}</h3>
                                        <div className={`flex ${isRtl ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full bg-green-500 ${isRtl ? 'ml-2' : 'mr-2'}`}></div>
                                                <span className="text-sm text-gray-600">{t('encaissements')}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full bg-red-500 ${isRtl ? 'ml-2' : 'mr-2'}`}></div>
                                                <span className="text-sm text-gray-600">{t('decaissements')}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full bg-blue-500 ${isRtl ? 'ml-2' : 'mr-2'}`}></div>
                                                <span className="text-sm text-gray-600">{t('cumulative_balance')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={soldeCumule}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="period"
                                                    tick={{ fill: '#6b7280' }}
                                                    axisLine={false}
                                                />
                                                <YAxis
                                                    tick={{ fill: '#6b7280' }}
                                                    axisLine={false}
                                                    tickFormatter={(value) => `${value / 1000}k`}
                                                    orientation={isRtl ? 'right' : 'left'}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '0.5rem',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                                    }}
                                                    formatter={(value, name) => [`${value} ${t('currency')}`, t(name.toLowerCase())]}
                                                />
                                                <Legend />
                                                <Bar
                                                    dataKey="encaissement"
                                                    fill="#10b981"
                                                    name={t('encaissements')}
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar
                                                    dataKey="décaissement"
                                                    fill="#ef4444"
                                                    name={t('decaissements')}
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Line
                                                    dataKey="solde"
                                                    stroke="#3b82f6"
                                                    strokeWidth={2}
                                                    name={t('cumulative_balance')}
                                                    dot={{ r: 4 }}
                                                    activeDot={{ r: 6 }}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Peak Days */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className={`flex items-center justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <h3 className="text-lg font-semibold text-gray-800">{t('max_inflow_day')}</h3>
                                            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                            </svg>
                                        </div>
                                        <div className={`flex items-end justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <div className={isRtl ? 'text-right' : 'text-left'}>
                                                <p className="text-3xl font-bold text-green-600">{maxEnc.toLocaleString()} {t('currency')}</p>
                                                <p className="text-gray-500 mt-1">{t('on_the')} {jourMaxEnc}</p>
                                            </div>
                                            <div className="text-green-500 bg-green-50 px-3 py-1 rounded-full text-sm font-medium">
                                                + {t('record')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className={`flex items-center justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <h3 className="text-lg font-semibold text-gray-800">{t('max_outflow_day')}</h3>
                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                            </svg>
                                        </div>
                                        <div className={`flex items-end justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <div className={isRtl ? 'text-right' : 'text-left'}>
                                                <p className="text-3xl font-bold text-red-600">{maxDec.toLocaleString()} {t('currency')}</p>
                                                <p className="text-gray-500 mt-1">{t('on_the')} {jourMaxDec}</p>
                                            </div>
                                            <div className="text-red-500 bg-red-50 px-3 py-1 rounded-full text-sm font-medium">
                                                - {t('record')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Reservation Stats Section */}
                    <div className={isRtl ? 'text-right' : 'text-left'}>
                        <div className={`flex items-center justify-between mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h2 className="text-xl font-semibold text-gray-800">{t('reservation_stats')}</h2>
                            <div className={`flex items-center ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <span className="w-3 h-3 rounded-full bg-indigo-500"></span>
                                <span className="text-sm text-gray-500">{t('performance')}</span>
                            </div>
                        </div>

                        {error.reservations ? (
                            <div className={`bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6 ${isRtl ? 'text-right' : 'text-left'}`}>
                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <svg className={`w-5 h-5 text-red-500 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="font-bold text-red-700">{t('error')}</p>
                                        <p className="text-red-600">{error.reservations}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Top Cars */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <SectionList
                                        title={t('top_5_rented_cars')}
                                        t={t}
                                        isRtl={isRtl}
                                        icon={
                                            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                            </svg>
                                        }
                                        data={reservationStats.topVoitures}
                                        loading={loading.reservations}
                                        render={v => (
                                            <div className={`flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                                    <div className={`bg-blue-100 p-2 rounded-lg ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-800">{v.marque} {v.modele}</p>
                                                        <p className="text-sm text-gray-500">{v.immatriculation}</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    {v.total_reservations} {t('locations_count')}
                                                </span>
                                            </div>
                                        )}
                                    />
                                    <SectionList
                                        title={t('top_5_least_used_cars')}
                                        t={t}
                                        isRtl={isRtl}
                                        icon={
                                            <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                                            </svg>
                                        }
                                        data={reservationStats.leastUsedCars}
                                        loading={loading.reservations}
                                        render={v => (
                                            <div className={`flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                                    <div className={`bg-purple-100 p-2 rounded-lg ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-800">{v.marque} {v.modele}</p>
                                                        <p className="text-sm text-gray-500">{v.immatriculation}</p>
                                                    </div>
                                                </div>
                                                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    {v.total_reservations} {t('locations_count')}
                                                </span>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* Top Clients */}
                                <SectionList
                                    title={t('top_5_vip_clients')}
                                    t={t}
                                    isRtl={isRtl}
                                    icon={
                                        <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                        </svg>
                                    }
                                    data={reservationStats.topClients}
                                    loading={loading.reservations}
                                    render={c => (
                                        <div className={`flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <div className={`flex items-center ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                                <div className={`bg-indigo-100 p-2 rounded-full ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800">{c.nom} {c.prenom}</p>
                                                    <p className="text-sm text-gray-500">{c.email}</p>
                                                </div>
                                            </div>
                                            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                                {c.total_depense.toLocaleString()} {t('currency')}
                                            </span>
                                        </div>
                                    )}
                                />

                                {/* Secondary Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className={`flex items-center justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <h3 className="text-lg font-semibold text-gray-800">{t('average_rental_duration')}</h3>
                                            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className={`flex items-end ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <p className={`text-3xl font-bold text-gray-800 ${isRtl ? 'ml-2' : 'mr-2'}`}>
                                                {parseFloat(reservationStats.dureeMoyenne).toFixed(1)}
                                            </p>
                                            <p className="text-lg text-gray-500 mb-1">{t('days')}</p>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                            <p className="text-sm text-gray-500">{t('avg_on_all_reservations')}</p>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <div className={`flex items-center justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <h3 className="text-lg font-semibold text-gray-800">{t('cancellation_rate')}</h3>
                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                        <div className={`flex items-end ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <p className={`text-3xl font-bold text-gray-800 ${isRtl ? 'ml-2' : 'mr-2'}`}>
                                                {reservationStats.tauxAnnulation}
                                            </p>
                                            <p className="text-lg text-gray-500 mb-1">%</p>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                            <p className="text-sm text-gray-500">{t('percentage_cancelled_reservations')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Sidebar>
    );
};

const StatCard = ({ title, value, icon, trend, t }) => {
    const isRtl = t('currency') === 'د.ج';
    const trendColors = {
        up: { bg: 'bg-green-100', text: 'text-green-600', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
        down: { bg: 'bg-red-100', text: 'text-red-600', icon: 'M13 17h8m0 0V9m0 8l-8-8-4 4-6-6' },
        neutral: { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' }
    };

    const trendData = trend ? trendColors[trend] : trendColors.neutral;

    return (
        <div className={`bg-white rounded-xl shadow-md p-5 ${isRtl ? 'text-right' : 'text-left'}`}>
            <div className={`flex justify-between items-start ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div>
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {typeof value === 'number' ? value.toLocaleString() + ' ' + t('currency') : value}
                    </p>
                </div>
                {icon && (
                    <div className="p-2 rounded-lg bg-opacity-20 bg-gray-200">
                        {icon}
                    </div>
                )}
            </div>
            {trend && (
                <div className={`mt-4 inline-flex items-center ${trendData.text} text-sm font-medium ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <svg className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={trendData.icon} />
                    </svg>
                    {trend === 'up' ? t('increase') : t('decrease')}
                </div>
            )}
        </div>
    );
};

const SectionList = ({ title, data, loading, render, icon, t, isRtl }) => (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden ${isRtl ? 'text-right' : 'text-left'}`}>
        <div className={`px-6 py-4 border-b border-gray-100 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
            {icon && <div className={isRtl ? 'ml-3' : 'mr-3'}>{icon}</div>}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        <div className="divide-y divide-gray-100">
            {loading ? (
                <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            ) : data.length > 0 ? (
                data.map((item, idx) => (
                    <div key={idx}>
                        {render(item)}
                    </div>
                ))
            ) : (
                <div className="text-center py-6">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="mt-2 text-sm font-medium text-gray-900">{t('no_data_available')}</h4>
                    <p className="mt-1 text-sm text-gray-500">{t('no_records_found_section')}</p>
                </div>
            )}
        </div>
    </div>
);

export default Analytics;